import { useQuery } from 'react-query';
import { useCallback, useMemo } from 'react';
import type { Permission, UseAuthorization } from './types';
import { getCurrentUserPermissions } from './api';
import { CURRENT_USER_PERMISSIONS_QUERY_KEY } from './queryKey';

/**
 * This hook is the base authorization implementation of the project.
 * It follows a similar way with the backend's rule engine.
 * It manages fetching and storing the permission list.
 * Additionally, provides a hasPermission function that checks one or multiple permissions.
 * If multiple permissions are passed, they will be evaluated with AND logic.
 *
 * Note: If you are checking the current account|organisation|site permission,
 * you can use usePermission hook which is a more refined solution.
 *
 * For more information: https://hotjar.atlassian.net/wiki/spaces/DEV/pages/2924412984/Authorisation
 */
export const useAuthorizationWithUserId = (userId: number | undefined): UseAuthorization => {
  const {
    data: currentUserPermissionsData,
    isFetched,
    isFetching,
    refetch,
  } = useQuery(CURRENT_USER_PERMISSIONS_QUERY_KEY, getCurrentUserPermissions, {
    enabled: !!userId,
    staleTime: 60 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const userPermissions = useMemo(
    () => (currentUserPermissionsData?.permissions ?? []) as Permission[],
    [currentUserPermissionsData?.permissions],
  );

  const hasPermission: UseAuthorization['hasPermission'] = useCallback(
    (resourceType, resourceId, ...permissions) => {
      if (!resourceId) {
        return false;
      }

      const calculatedPermissions = permissions.map<Permission>(
        (permission) => `${resourceType}:${resourceId}:${permission}`,
      );
      return calculatedPermissions.every((p) => userPermissions.includes(p));
    },
    [userPermissions],
  );

  return {
    hasPermission,
    isPermissionFetched: isFetched,
    isPermissionFetching: isFetching,
    refetchPermissions: refetch,
  };
};
